import group from "../../../../../images/common/group";

function oneline(strings, ...values) {
  return strings
    .map((str, i) => `${str}${values[i] || ''}`)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

// let test = generateQuery({
//   analysis_interval: {
//     value: 1,
//     unit: 'Days'
//   },
//   time: {
//     start: '2023-01-01',
//     end: '2023-01-31'
//   },
//   // query_sql: sqlTemplates['minimumValue'](['field_name']),
//   workspace: {
//     id: 'my_workspace'
//   },
//   tableName: 'application_data'
// })

export const generateQuery = ({
  analysis_interval,
  time,
  query_sql,
  workspace,
  metric,
  groupBy,
  label,
  index,
  analysisMode,
  chartValues,
}) => {
  // Handle multiple group by fields
  const groupByFields = groupBy ? groupBy.split(',').map(field => field.trim()).filter(Boolean) : [];
  
  let selectFields = [];
  let groupByColumns = [];
  
  if (groupByFields.length > 0) {
    groupByFields.forEach((field) => {
      if (field === 'time') {
        const timeBucket = `time_bucket('${parseInt(analysisMode?.quantity)} ${analysisMode?.value}'::interval, time)`;
        selectFields.push(`${timeBucket} AS bucketed_time`);
        groupByColumns.push('bucketed_time');
      } else {
        // For non-time fields, include them in SELECT and GROUP BY
        selectFields.push(`${field}`);
        groupByColumns.push(field);
      }
    });
  }

  // For table charts in raw mode, use the actual field name as alias
  // Otherwise use the stream label or default to stream_N
  const isTableRawMode = chartValues?.chartType === 'table' && chartValues?.tableColumnMetricData === 'raw';
  const metricFieldName = metric?.metrics?.[0]; // Get the first (and only for raw mode) metric
  
  let labelName;
  if (isTableRawMode && metricFieldName) {
    labelName = metricFieldName; // Use actual field name for raw mode
  } else {
    labelName = label?.trim() ? label.trim() : `stream_${index + 1}`;
  }
  
  const safeAlias = `"${String(labelName).replace(/"/g, '""')}"`;
  
  // Prefer explicit stream time; if missing, fall back to chart-level dateRange
  const effectiveTime = (time && time.start)
    ? time
    : (chartValues?.dateRange && chartValues.dateRange.start)
      ? chartValues.dateRange
      : null

  let whereClause = effectiveTime
    ? `time BETWEEN '${effectiveTime.start}'::timestamp AND '${effectiveTime.end}'::timestamp`
    : '1=1'

  let allMetrics = chartValues?.dataStreams?.streams?.map(stream => stream.metric.metrics);
  allMetrics = allMetrics?.flat();

  if (chartValues?.chartType === 'table' && allMetrics?.length > 0) {
    const nonTimeColumns = allMetrics.filter(col => col !== 'time');
    if (nonTimeColumns.length > 0) {
      const coalesceFields = nonTimeColumns.join(', ');
      whereClause = effectiveTime
        ? `${whereClause}\n  AND (${coalesceFields}) IS NOT NULL`
        : `(${coalesceFields}) IS NOT NULL`;
    }
  }

  // Build GROUP BY and ORDER BY clauses with all group by fields
  const groupByClause = groupByColumns.length > 0 ? `GROUP BY ${groupByColumns.join(', ')}` : '';
  const orderByClause = groupByColumns.length > 0 ? `ORDER BY ${groupByColumns.join(', ')} ASC` : '';

  // Build SELECT clause
  const selectClause = selectFields.length > 0 ? `${selectFields.join(', ')}, ` : '';

  return `
    SELECT ${selectClause}${query_sql} AS ${safeAlias}
    FROM "${workspace.id}"."${metric.selectedTableName}_view"
    WHERE ${whereClause}
    ${groupByClause}
    ${orderByClause}
  `
};


export const QUERY_TEMPLATES = {
  minimumValue: ({ time = { start: '', end: '' }, metric = {} }, workspace) => {
    const metricFields = metric?.metrics || [];

    const metricSelections = metricFields.map(field => {
      if (field === 'time') {
        return `MIN(time) AS ${field}`;
      }
      return `COUNT(${field}) AS ${field}`;
    }).join(',\n       ');

    return `
      SELECT time_bucket('1 Days'::interval, time) AS bucketed_time,
             ${metricSelections}
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      WHERE ${time.start ? `time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : '1=1'}
      GROUP BY bucketed_time
      ORDER BY bucketed_time ASC;
    `;
  },
  maximumValue: ({ time = { start: '', end: '' }, metric = {} }, workspace) => {
    const metricFields = metric?.metrics || [];

    const metricSelections = metricFields.map(field => {
      if (field === 'time') {
        return `to_timestamp(AVG(EXTRACT(EPOCH FROM ${field}))) AS ${field}`;
      }
      return `AVG(${field}) AS ${field}`;
    }).join(',\n       ');

    return `
      SELECT
        ${metricSelections}
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      WHERE ${time.start ? `time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : '1=1'}
    `;
  },
  averageValue: ({ time = { start: '', end: '' }, metric = {} }, workspace) => {
    const metricFields = metric?.metrics || [];

    const metricSelections = metricFields
      .map(field => `AVG(${field}) AS ${field}`)
      .join(',\n       ');

    return `
      SELECT 
        time_bucket('1 Days'::interval, time) AS bucketed_time,
        ${metricSelections}
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      ${time.start ? `WHERE time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : ''}
      GROUP BY bucketed_time
      ORDER BY bucketed_time ASC;
    `;
  },
  totalSum: () => ``,
  totalCount: () => ``,
  uniqueCount: () => ``,
  allTrue: () => ``,
  anyTrue: () => ``,
  peakHourOfDay: ({ metric, time = { start: '', end: '' } }, workspace) => {
    const metricFields = metric?.metrics || [];
    const sumExpr = metricFields.map(col => `SUM(COALESCE("${col}", 0))`).join(' + ');
    return `
      WITH hourly AS (
        SELECT
          date_trunc('hour', time) AS bucketed_hour,
          date_trunc('day', time) AS bucketed_day,
          ${sumExpr} AS total_metric
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
        ${time.start ? `WHERE time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : ''}
        GROUP BY bucketed_hour, bucketed_day
      ), ranked AS (
        SELECT
          bucketed_hour,
          bucketed_day,
          total_metric,
          ROW_NUMBER() OVER (PARTITION BY bucketed_day ORDER BY total_metric DESC, bucketed_hour ASC) AS rn
        FROM hourly
      )
      SELECT bucketed_day AS bucketed_time,
             EXTRACT(HOUR FROM bucketed_hour) AS peak_value
      FROM ranked
      WHERE rn = 1
      ORDER BY bucketed_time;
    `;
  },
  topNContributors: ({ schema, table, metric, groupBy, topN }, workspace) => {
    const metricFields = metric?.metrics || [];

    const totalMetricExpr = metricFields
      .map(field => `SUM(${field})`)
      .join(' + '); // SUM(field1) + SUM(field2) + ...

    return `
      SELECT ${groupBy}, ${totalMetricExpr} AS total_metric
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      GROUP BY ${groupBy}
      ORDER BY total_metric DESC
      LIMIT ${topN};
    `;
  },
  rollingNDayAverage: ({ metric, time = { start: '', end: '' }, analysisInterval, windowDays }, workspace) => {
    const metricFields = metric?.metrics || [];
    const columnSums = metricFields.length
      ? metricFields.map(col => `COALESCE("${col}", 0)`).join(' + ')
      : '0';
    const qty = Number(analysisInterval?.value) || 1;
    const unit = (analysisInterval?.unit || 'day').toLowerCase();
    const unitPlural = unit.endsWith('s') ? unit : `${unit}s`;
    const bucketInterval = `${qty} ${unitPlural}`;
    const windowSize = Math.max(1, Number(windowDays) || 7);
    // Build dynamic sum of LAG terms to avoid ROWS frame syntax
    const lagTerms = Array.from({ length: windowSize }, (_, i) => (
      i === 0
        ? 'COALESCE(period_total, 0)'
        : `COALESCE(LAG(period_total, ${i}) OVER (ORDER BY bucketed_time), 0)`
    )).join(' + ');
    return `
      WITH grouped AS (
        SELECT time_bucket('${bucketInterval}'::interval, time) AS bucketed_time,
               SUM(${columnSums}) AS period_total
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
        ${time.start ? `WHERE time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : ''}
        GROUP BY bucketed_time
      ), windowed AS (
        SELECT
          bucketed_time,
          period_total,
          ROW_NUMBER() OVER (ORDER BY bucketed_time) AS rn
        FROM grouped
      )
      SELECT
        bucketed_time,
        (${lagTerms}) / NULLIF(LEAST(rn, ${windowSize}), 0)::float AS rolling_avg
      FROM windowed
      ORDER BY bucketed_time ASC;
    `;
  },
  nthPercentile: ({ metric, percentile }, workspace) => {
    const metricFields = metric?.metrics || [];

    const percentileExprs = metricFields
      .map(field => `PERCENTILE_CONT(${percentile / 100.0}) WITHIN GROUP (ORDER BY ${field}) AS ${field}_p${percentile}`)
      .join(',\n       ');

    return `
      SELECT ${percentileExprs}
      FROM "${workspace.id}"."${metric.selectedTableName}_view";
    `;
  },
  dayOverDayChange: ({ metric }, workspace) => {
    const field = metric.metrics?.[0]; // Only 1 field supported for LAG-based logic
    return `
      SELECT
        time::date AS day,
        ${field},
        LAG(${field}, 1, 0) OVER (ORDER BY time::date) AS previous_day_metric,
        (${field} - LAG(${field}, 1, 0) OVER (ORDER BY time::date)) * 100.0
          / NULLIF(LAG(${field}, 1, 0) OVER (ORDER BY time::date), 0) AS day_over_day_change
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      ORDER BY time::date;
    `;
  },

  weekOverWeekChange: ({ metric }, workspace) => {
    const field = metric.metrics?.[0]; // Only 1 field supported for LAG-based logic
    return `
      SELECT
        date_trunc('week', time) AS week,
        SUM(${field}) AS current_week_metric,
        LAG(SUM(${field}), 1, 0) OVER (ORDER BY date_trunc('week', time)) AS previous_week_metric,
        (
          SUM(${field}) - LAG(SUM(${field}), 1, 0) OVER (ORDER BY date_trunc('week', time))
        ) * 100.0 / NULLIF(LAG(SUM(${field}), 1, 0) OVER (ORDER BY date_trunc('week', time)), 0)
        AS week_over_week_change
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      GROUP BY date_trunc('week', time)
      ORDER BY date_trunc('week', time);
    `;
  },

  cumulativeTotal: ({ metric, time = { start: '', end: '' }, analysisInterval }, workspace) => {
    const metricFields = metric?.metrics || [];
    
    if (metricFields.length === 0) {
      throw new Error('No metrics provided for cumulative total calculation');
    }
    
    // Use CTE approach to handle grouped data properly
    const columnSums = metricFields.map(col => `COALESCE("${col}", 0)`).join(' + ');
    const qty = Number(analysisInterval?.value) || 1;
    const unit = (analysisInterval?.unit || 'day').toLowerCase();
    const unitPlural = unit.endsWith('s') ? unit : `${unit}s`;
    const bucketInterval = `${qty} ${unitPlural}`;
    return `
      WITH grouped_data AS (
        SELECT time_bucket('${bucketInterval}'::interval, time) AS bucketed_time,
               SUM(${columnSums}) AS period_total
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
        ${time.start ? `WHERE time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : ''}
        GROUP BY bucketed_time
      )
      SELECT bucketed_time,
             SUM(period_total) OVER (ORDER BY bucketed_time ASC) AS cumulative_total
      FROM grouped_data
      ORDER BY bucketed_time ASC;
    `;
  },

  ratioOfTwoMetrics: ({ metric, numerator, denominator }, workspace) => `
    SELECT time,
           CASE WHEN ${denominator} = 0 THEN NULL ELSE ${numerator} * 1.0 / ${denominator} END AS ratio
    FROM "${workspace.id}"."${metric.selectedTableName}_view"
    ORDER BY time;
  `,

  slaBreachesAboveThreshold: ({ metric, comparator, threshold }, workspace) => {
    const field = metric.metrics?.[0]; // Only 1 field allowed in WHERE
    return `
      SELECT time, ${field}
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      WHERE ${field} ${comparator} ${threshold};
    `;
  },

  anomaly: ({ metric, timeRangeStart, timeRangeEnd }, workspace) => {
    const field = metric.metrics?.[0]; // Only 1 field supported for z-score
    return `
      WITH metric_avg_stddev AS (
        SELECT AVG(${field}) AS avg_metric, STDDEV(${field}) AS stddev_metric
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
        WHERE time BETWEEN '${timeRangeStart}'::timestamp AND '${timeRangeEnd}'::timestamp
      ), metric_with_zscore AS (
        SELECT time, ${field},
          (${field} - (SELECT avg_metric FROM metric_avg_stddev)) /
          NULLIF((SELECT stddev_metric FROM metric_avg_stddev), 0) AS z_score
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
      )
      SELECT time, ${field}, z_score
      FROM metric_with_zscore
      WHERE ABS(z_score) > 3
      ORDER BY time;
    `;
  },

  seasonalityHeatmap: ({ metric, timeUnit }, workspace) => {
    const avgExpr = metric.metrics.map(field => `AVG(${field}) AS avg_${field}`).join(',\n       ');
    return `
      SELECT EXTRACT(${timeUnit} FROM time) AS time_component,
             ${avgExpr}
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      GROUP BY time_component
      ORDER BY time_component;
    `;
  },

  trendline: ({ metric, time = { start: '', end: '' }, analysisInterval }, workspace) => {
    const field = metric.metrics?.[0]; // Use the first metric as Y
    const qty = Number(analysisInterval?.value) || 1;
    const unit = (analysisInterval?.unit || 'day').toLowerCase();
    const unitPlural = unit.endsWith('s') ? unit : `${unit}s`;
    const bucketInterval = `${qty} ${unitPlural}`;
    return `
      WITH grouped AS (
        SELECT time_bucket('${bucketInterval}'::interval, time) AS bucketed_time,
               SUM(COALESCE("${field}", 0)) AS y
        FROM "${workspace.id}"."${metric.selectedTableName}_view"
        ${time.start ? `WHERE time BETWEEN '${time.start}'::timestamp AND '${time.end}'::timestamp` : ''}
        GROUP BY bucketed_time
      )
      SELECT
        bucketed_time,
        REGR_SLOPE(y, EXTRACT(EPOCH FROM bucketed_time)) OVER () * EXTRACT(EPOCH FROM bucketed_time)
          + REGR_INTERCEPT(y, EXTRACT(EPOCH FROM bucketed_time)) OVER () AS trendline
      FROM grouped
      ORDER BY bucketed_time;
    `;
  },

  compareToTarget: ({ metric, targetValue }, workspace) => {
    const field = metric.metrics?.[0]; // Only 1 field compared to target
    return `
      SELECT time, ${field},
        CASE
          WHEN ${field} > ${targetValue} THEN 'Above Target'
          WHEN ${field} < ${targetValue} THEN 'Below Target'
          ELSE 'At Target'
        END AS target_status
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      ORDER BY time;
    `;
  },

  sumColumns: ({ metric }, workspace) => {
    const expr = metric.metrics.join(" + ");
    return `
      SELECT time, ${expr} AS sum_of_metrics
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      ORDER BY time;
    `;
  },

  subtractColumns: ({ metric, numerator: minuend, denominator: subtrahend }, workspace) => `
    SELECT time, ${minuend} - ${subtrahend} AS difference
    FROM "${workspace.id}"."${metric.selectedTableName}_view"
    ORDER BY time;
  `,

  multiplyColumns: ({ metric }, workspace) => {
    const expr = metric.metrics.join(" * ");
    return `
      SELECT time, ${expr} AS product_of_metrics
      FROM "${workspace.id}"."${metric.selectedTableName}_view"
      ORDER BY time;
    `;
  },

  divideColumns: ({ numerator, denominator, metric }, workspace) => `
    SELECT
      time,
      CASE
        WHEN ${denominator} = 0 THEN NULL
        ELSE ${numerator} * 1.0 / ${denominator}
      END AS ratio
    FROM "${workspace.id}"."${metric.selectedTableName}_view"
    ORDER BY time;
  `,
  customSql: () => ``,
}

const defaultAggregations = (columns = []) => {
  return columns.map((field) => {
    if (field === 'time') {
      return `MIN(${field}) AS ${field}`;
    }
    return `COUNT(${field}) AS ${field}`;
  }).join(',\n  ');
};

export const sqlTemplates = {
  minimumValue: (columns) => columns.length === 1 ? `MIN(${columns[0]})` : `MIN(LEAST(${columns.join(', ')}))`,
  maximumValue: (columns) => columns.length === 1 ? `MAX(${columns[0]})` : `MAX(GREATEST(${columns.join(', ')}))`,
  averageValue: (columns) => `AVG((${columns.join(' + ')}) / ${columns.length})`,
  firstValue: (columns) => columns.length === 1 ? `first(${columns[0]},time)` : `first((${columns.join('+ ')}),time)`,
  lastValue: (columns) => columns.length === 1 ? `last(${columns[0]},time)` : `last((${columns.join('+ ')}),time)`,
  totalSum: (columns) => `SUM(${columns.join(' + ')})`,
  totalCount: (columns) => `COUNT(${columns.join(' + ')})`,
  uniqueCount: (columns) => columns.length === 1 ? `COUNT(DISTINCT ${columns[0]})` : `COUNT(DISTINCT (${columns.join(', ')}))`,
  allTrue: (columns) => `(${columns.join(' AND ')})`,
  anyTrue: (columns) => `(${columns.join(' OR ')})`,
  peakHourOfDay: (columns) => `FIRST_VALUE(EXTRACT(HOUR FROM time)) OVER (PARTITION BY DATE(time) ORDER BY (${columns.join(' + ')}) DESC)`,
  topNContributors: (columns, metrics) => `SELECT ${columns.join(', ')}, SUM(${columns.join(' + ')}) AS total_contribution FROM table_name GROUP BY ${columns.join(', ')} ORDER BY total_contribution DESC LIMIT ${metrics?.limit || 10}`,
  rollingAverage: (columns, metrics) => `AVG(${columns.join(' + ')}) OVER (ORDER BY time ASC ROWS BETWEEN ${metrics.window || 7} PRECEDING AND CURRENT ROW)`,
  nthPercentile: (columns, metrics) => `PERCENTILE_CONT(${(metrics.percentile || 50) / 100.0}) WITHIN GROUP (ORDER BY ${columns.join(' + ')})`,
  dayOverDayChange: (column) => `(${column} - LAG(${column}, 1, 0) OVER (ORDER BY time::date)) * 100.0 / NULLIF(LAG(${column}, 1, 0) OVER (ORDER BY time::date), 0)`,
  weekOverWeekChange: (column) => `SELECT date_trunc('week', time) AS week, (SUM(${column}) - LAG(SUM(${column}), 1, 0) OVER (ORDER BY date_trunc('week', time))) * 100.0 / NULLIF(LAG(SUM(${column}), 1, 0) OVER (ORDER BY date_trunc('week', time)), 0) AS week_over_week_change FROM table_name GROUP BY date_trunc('week', time)`,
  cumulativeTotal: (columns) => {
    if (!columns || columns.length === 0) {
      return 'SUM(1)';
    }
    const columnSums = columns.map(col => `COALESCE("${col}", 0)`).join(' + ');
    return `SUM(${columnSums})`;
  },
  ratioOfTwoMetrics: (columns) => {
    const [num, den] = columns;
    return `CASE WHEN SUM(COALESCE(${den}, 0)) = 0 THEN NULL ELSE SUM(COALESCE(${num}, 0)) * 1.0 / NULLIF(SUM(COALESCE(${den}, 0)), 0) END`;
  },
  slaBreachesAboveThreshold: (column, metrics) => {
    if (!column || typeof column !== 'string') {
      return 'NULL';
    }
    if (!metrics || typeof metrics.threshold === 'undefined') {
      return 'NULL';
    }
    
    // For SLA breaches with GROUP BY, aggregate the column first
    const aggregatedColumn = `SUM(COALESCE("${column}", 0))`;
    const comparator = metrics.comparator || '>';
    const threshold = metrics.threshold || 0;
    
    return `CASE WHEN ${aggregatedColumn} ${comparator} ${threshold} THEN ${aggregatedColumn} ELSE NULL END`;
  },
  anomaly: (column, metrics) => `(${column} - AVG(${column}) OVER ()) / NULLIF(STDDEV(${column}) OVER (), 0)`,
  seasonalityHeatmap: (columns, metrics) => `SELECT EXTRACT(${metrics?.timeUnit || 'HOUR'} FROM time) AS time_component, AVG(${columns.join(' + ')}) AS avg_value FROM table_name GROUP BY EXTRACT(${metrics?.timeUnit || 'HOUR'} FROM time)`,
  trendline: (column) => {
    if (!column || typeof column !== 'string') {
      return 'SUM(1)';
    }
    
    // For trendline with GROUP BY, aggregate the column and use the time_bucket expression
    const aggregatedColumn = `SUM(COALESCE("${column}", 0))`;
    const timeValue = `EXTRACT(EPOCH FROM time_bucket('1 Days'::interval, time))`;
    return `REGR_SLOPE(${aggregatedColumn}, ${timeValue}) OVER () * ${timeValue} + REGR_INTERCEPT(${aggregatedColumn}, ${timeValue}) OVER ()`;
  },
  compareToTarget: (column, metrics) => `CASE WHEN ${column} > ${metrics?.targetValue || 0} THEN ${column} WHEN ${column} < ${metrics?.targetValue || 0} THEN -${column} ELSE 0 END`,
  sumColumns: (columns) => {
    const expr = columns.map(c => `COALESCE(${c}, 0)`).join(' + ');
    return `SUM(${expr})`;
  },
  subtractColumns: (columns) => {
    if (!columns || columns.length === 0) return 'NULL';
    if (columns.length === 1) return `SUM(COALESCE(${columns[0]}, 0))`;
    const first = `SUM(COALESCE(${columns[0]}, 0))`;
    const rest = columns.slice(1).map(c => `SUM(COALESCE(${c}, 0))`).join(' + ');
    return `${first} - (${rest})`;
  },
  multiplyColumns: (columns) => {
    // Sum of row-wise product across the group
    const product = columns.map(c => `COALESCE(${c}, 0)`).join(' * ');
    return `SUM(${product})`;
  },
  divideColumns: (columns) => {
    const [num, den] = columns;
    return `CASE WHEN SUM(COALESCE(${den}, 0)) = 0 THEN NULL ELSE SUM(COALESCE(${num}, 0)) * 1.0 / NULLIF(SUM(COALESCE(${den}, 0)), 0) END`;
  },
  customSql: (sql = '') => `${sql}`,
  selectSql: (columns) => `${columns.join(', ')}`,
}